/**
 * Colour-limit helpers shared by the generator UI, config normalization,
 * and seeded palette generators. Hard minimum is 2 so a canvas cannot
 * collapse into a single colour.
 */

export const COLOR_LIMIT_MIN = 2;
export const COLOR_LIMIT_MAX = 8;

/** Clamp a colour-limit value into the allowed [2, 8] range. */
export function normalizeColorLimit(value: unknown, fallback: number = COLOR_LIMIT_MIN): number {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) {
    return Math.max(COLOR_LIMIT_MIN, Math.min(COLOR_LIMIT_MAX, fallback));
  }
  return Math.max(COLOR_LIMIT_MIN, Math.min(COLOR_LIMIT_MAX, n));
}

/**
 * How many colours a seeded palette generator should produce for this
 * generation, given the existing colour-limit controls.
 *
 * - manual: exactly the clamped colourLimit
 * - auto / off: leave room for the backend Auto slicer by producing a
 *   full aesthetic set (max); Auto still decides the final displayed count
 */
export function resolvePaletteGenerationCount(opts: {
  colorLimitMode?: string | null;
  colorLimit?: number | null;
  /** Seeded RNG used only when mode is off (variable aesthetic size). */
  random?: () => number;
}): number {
  const mode = opts.colorLimitMode || 'off';
  if (mode === 'manual') {
    return normalizeColorLimit(opts.colorLimit);
  }
  if (mode === 'auto') {
    // Backend Auto picks the final count; generate the full aesthetic max.
    return COLOR_LIMIT_MAX;
  }
  // off: variable aesthetic size between min and 6, seeded when random is given.
  const random = opts.random || (() => 0.5);
  const offMax = 6;
  const span = offMax - COLOR_LIMIT_MIN + 1;
  return COLOR_LIMIT_MIN + Math.floor(random() * span);
}
